import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useIdentity } from '@/stores/identity';
import type { AppRole } from '@/lib/types';

/**
 * RouteGuard
 *
 * Wrap protected routes. Renders children only when the caller is signed in
 * AND their role is in `allowedRoles` (if specified).
 *
 * Per Phase 1 "Never Again" rules:
 *  - No `?preview=true` bypass. There is no escape hatch.
 *  - No `if (error) return <>{children}</>` style fail-open. On any non-
 *    authenticated state, redirect to /auth/sign-in with `from` set so we
 *    can return to the original route after sign-in.
 *  - Role check fails closed: unknown role -> redirect.
 *
 * Status handling:
 *  - 'unknown' / 'loading' -> render a tiny loading state (don't redirect yet,
 *    or we'd flash sign-in on every page load before the session resumes).
 *  - 'authenticated' + role check passes -> render children.
 *  - 'authenticated' + role check fails -> redirect to / (their default route).
 *  - 'guest' / 'error' -> redirect to /auth/sign-in.
 */
export interface RouteGuardProps {
  children: ReactNode;
  /**
   * Allowed app_roles. If omitted, any authenticated user passes.
   * Examples:
   *   ['owner']                    - owner only
   *   ['owner', 'manager']         - owner or manager
   *   ['owner', 'manager', 'staff']- any tenant user
   */
  allowedRoles?: AppRole[];
}

export function RouteGuard({ children, allowedRoles }: RouteGuardProps) {
  const status = useIdentity((s) => s.status);
  const role = useIdentity((s) => s.identity?.role);
  const isPlatformAdmin = useIdentity(
    (s) => s.identity?.flags?.is_platform_admin ?? false
  );
  // Whether we already have a cached identity object. If we do, a transient
  // 'loading' status (TOKEN_REFRESHED on tab refocus, USER_UPDATED, etc.)
  // should NOT unmount the route subtree — that would wipe in-progress
  // form state every time the user tabs away and back.
  const hasIdentity = useIdentity((s) => s.identity !== null);
  const location = useLocation();

  if ((status === 'unknown' || status === 'loading') && !hasIdentity) {
    return <LoadingFallback />;
  }

  if (status === 'guest' || status === 'error') {
    return (
      <Navigate
        to="/auth/sign-in"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  // status === 'authenticated' from here
  // Platform admins always pass — they have cross-org powers regardless of
  // their tenant role. This lets an org's owner who is also a platform admin
  // access /admin/* without changing their tenant role.
  if (allowedRoles && role && !allowedRoles.includes(role) && !isPlatformAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
      Loading...
    </div>
  );
}

/**
 * GuestOnly
 *
 * Inverse of RouteGuard. Wrap routes that should only render when no one is
 * signed in (e.g. /auth/sign-in). If an authenticated user lands here, redirect
 * them to /.
 */
export function GuestOnly({ children }: { children: ReactNode }) {
  const status = useIdentity((s) => s.status);
  const hasIdentity = useIdentity((s) => s.identity !== null);

  if ((status === 'unknown' || status === 'loading') && !hasIdentity) {
    return <LoadingFallback />;
  }

  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
